import './application.css'
// The public brand, expressed as strictly-outranking overrides of the custom
// properties application.css just defined — the bridge wins on specificity,
// never on load order, so production chunk order cannot invert it.
import '../shared/styles/admin-brand.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createInertiaApp } from '@inertiajs/react'
import { ThemeProvider, type PersistDisplayPreferences } from '../shared/theme/ThemeProvider'
import { jsonFetch } from '../shared/lib/jsonFetch'

// Authenticated surface: persist changed display preferences server-side.
// shared/ThemeProvider stays surface-agnostic and never knows this endpoint.
// A plain JSON request, NOT an Inertia visit: the endpoint answers JSON and
// carries no X-Inertia header, which Inertia would surface as an error dialog.
// Fire-and-forget — localStorage already holds the applied value.
const persistDisplayPreferences: PersistDisplayPreferences = (prefs) => {
  void jsonFetch('/preferences/display', {
    method: 'PATCH',
    body: { display_preferences: prefs },
  })
}

void createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob<{ default: React.ComponentType }>([
      '../pages/admin/**/*.tsx',
      '!../pages/admin/**/*.{test,spec}.tsx',
    ])
    const loadPage = pages[`../pages/admin/${name}.tsx`]
    if (!loadPage) throw new Error(`Unknown admin page: ${name}`)
    return (await loadPage()).default
  },
  // Each page passes its own `title` to AdminShell, which renders <Head>. This
  // appends the service name so a caseworker with several tabs open can tell
  // them apart, and supplies the bare name for anything that has not set one.
  title: (title) => (title ? `${title} – Mesterbrev` : 'Mesterbrev'),
  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: "brackets" }
    },
  },
  setup({ el, App, props }) {
    createRoot(el!).render(
      <React.StrictMode>
        <ThemeProvider onPersist={persistDisplayPreferences}>
          <App {...props} />
        </ThemeProvider>
      </React.StrictMode>,
    )
  },
})
